import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Search } from 'lucide-react';
import { Avatar } from '@components/avatar';
import { SchedulerConflicts } from '@components/scheduler/sidebar/conflicts';
import { cn } from '@utils/cn';
import { DEFAULT_FILTERS } from '../constants';
import { FilterChips } from '../filterChips';
import { matchesSearch } from '../helpers';
import { ConflictLink } from '../conflictLink';
import { ResourceListProps } from './types';

export const ResourceList = ({
  options,
  checked,
  pinned = [],
  single,
  onPick,
  searchPlaceholder,
  conflictTitle,
  autoFocus,
  listClassName,
  boxClassName,
  headerClassName,
  footer,
}: ResourceListProps) => {
  const { t } = useTranslation('app');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const visible = options
    .filter(
      (el) =>
        checked.includes(el.value) ||
        pinned.includes(el.value) ||
        ((!filters.qualified || el.qualified !== false) &&
          (!filters.available || !(el.conflictDays ?? el.conflicts.length))),
    )
    .filter((el) => matchesSearch(`${el.label} ${el.sublabel || ''}`, search))
    .sort((a, b) => Number(pinned.includes(b.value)) - Number(pinned.includes(a.value)));

  return (
    <>
      <label className="flex h-10 shrink-0 items-center gap-2 rounded-md border-2 border-primary-500 bg-white px-2.5">
        <Search className="h-4 w-4 shrink-0 text-neutral-400" />
        <input
          autoFocus={autoFocus}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className="min-w-0 flex-1 bg-transparent text-base text-neutral-800 outline-none placeholder:text-neutral-400"
        />
      </label>
      <div className={boxClassName}>
        <div className={cn('px-2 pb-1 pt-2', headerClassName)}>
          <FilterChips value={filters} onChange={setFilters} keys={['qualified', 'available']} />
        </div>
        <div role={single ? 'listbox' : undefined} className={cn('p-1', listClassName)}>
          {visible.length ? (
            visible.map((option) => {
              const isChecked = checked.includes(option.value);
              return (
                <button
                  key={option.value}
                  type="button"
                  role={single ? 'option' : 'checkbox'}
                  aria-checked={single ? undefined : isChecked}
                  aria-selected={single ? isChecked : undefined}
                  onClick={() => onPick(option.value)}
                  className={cn(
                    'flex min-h-11 w-full items-center gap-2.5 rounded px-2 py-1 text-left',
                    isChecked && 'bg-primary-50',
                  )}>
                  {!single && (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border-[1.5px] border-neutral-400 bg-white">
                      {isChecked && (
                        <Check className="h-4 w-4 text-primary-500" strokeWidth={2.6} />
                      )}
                    </span>
                  )}
                  <Avatar name={option.label} hideTooltip className="h-7 w-7 text-xs" />
                  <span className="flex min-w-0 flex-1 items-center gap-1.5 text-sm font-medium text-neutral-600">
                    <span className="truncate">{option.label}</span>
                    {!!option.conflictDays &&
                      (option.onShowConflicts ? (
                        <ConflictLink
                          compact
                          count={option.conflictDays}
                          onClick={option.onShowConflicts}
                        />
                      ) : (
                        <AlertTriangle
                          role="img"
                          aria-label={t('schedule.conflicts')}
                          className="h-3.5 w-3.5 shrink-0 text-warning-500"
                        />
                      ))}
                    {option.conflictDays === undefined && !!option.conflicts.length && (
                      <SchedulerConflicts
                        name={option.label}
                        title={conflictTitle}
                        conflicts={option.conflicts}
                        iconClassName="text-sm"
                      />
                    )}
                  </span>
                  {option.sublabel && (
                    <span className="shrink-0 text-[11px] text-neutral-400">{option.sublabel}</span>
                  )}
                </button>
              );
            })
          ) : (
            <p className="px-2 py-2 text-sm text-neutral-400">
              {t('forms.default.multiSelect.empty')}
            </p>
          )}
        </div>
        {footer}
      </div>
    </>
  );
};
